import { FormControlLabel } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import Box from '@mui/material/Box';
import ModalFooter from '../common/ModalFooter';
import Checkbox from '../common/Checkbox';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';
import Pill from '../common/Pill';

type CategoryFormValues = {
  categoryName: string;
  primary: boolean;
  sortOrder: string;
  sumpTank: boolean;
};

interface EditLubricantCategoryModalProps {
  onClose: () => void;
  onSubmit: () => void;
  control: Control<CategoryFormValues>;
  errors?: FieldErrors<CategoryFormValues>;
  isEditMode?: boolean;
  isSubmitting?: boolean;
}

const EditLubricantCategoryModal: React.FC<EditLubricantCategoryModalProps> = ({
  onClose,
  onSubmit,
  control,
  errors,
  isEditMode = false,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={true}
      onClose={onClose}
      size="sm"
      title={isEditMode ? t('lubricants.editCategoryTitle') : t('lubricants.addCategoryTitle')}
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={onSubmit}
          isLoading={isSubmitting}
          submitText={
            isSubmitting
              ? isEditMode
                ? t('buttons.updating')
                : t('buttons.adding')
              : isEditMode
                ? t('buttons.update')
                : t('buttons.add')
          }
        />
      }
    >
      <FormField label={t('form.categoryName')} sx={{ mt: 2 }} required>
        <Controller
          name="categoryName"
          control={control}
          rules={{ required: t('form.categoryNameRequired') }}
          render={({ field }) => (
            <InputField
              {...field}
              value={field.value || ''}
              placeholder={t('form.enterCategoryName')}
              error={!!errors?.categoryName}
              helperText={errors?.categoryName?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.showOrder')} sx={{ mt: 2 }} required>
        <Controller
          name="sortOrder"
          control={control}
          render={({ field }) =>
            isEditMode ? (
              <InputField
                {...field}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder={t('form.enterShowOrder')}
                type="number"
              />
            ) : (
              <Pill>{field.value}</Pill>
            )
          }
        />
      </FormField>

      <Box mt={3}>
        <Controller
          name="primary"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ mr: 2, ml: 2 }}
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={t('form.primary')}
            />
          )}
        />
      </Box>

      <Box mt={3}>
        <Controller
          name="sumpTank"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ mr: 2, ml: 2 }}
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={t('form.sumpTank')}
            />
          )}
        />
      </Box>
    </Modal>
  );
};

export default EditLubricantCategoryModal;
